import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { login as loginService, logout as logoutService, getCurrentUser, getUser } from '../services/auth'

/**
 * User interface
 */
export interface User {
  id: string
  name: string
  email: string
  role: 'super_admin' | 'admin' | 'owner' | 'user'
  isActive: boolean
  lastLogin?: string
  createdAt: string
  parentAccountId?: string
  permissions?: string[]
}

/**
 * Auth context interface
 */
interface AuthContextType {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

/**
 * Auth context
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Auth provider props
 */
interface AuthProviderProps {
  children: ReactNode
}

/**
 * Auth provider component
 * @param props - Component props
 * @returns Auth provider
 */
export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  /**
   * Initializes auth state from storage
   */
  useEffect(() => {
    initializeAuth()
  }, [])

  /**
   * Initializes authentication from stored data
   * @returns Promise void
   */
  async function initializeAuth(): Promise<void> {
    try {
      const storedUser = getUser()
      if (storedUser) {
        setUser(storedUser)
        try {
          const currentUser = await getCurrentUser()
          setUser(currentUser)
        } catch (error) {
          console.error('Error refreshing user:', error)
          setUser(null)
        }
      }
    } catch (error) {
      console.error('Error initializing auth:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Logs in user
   * @param email - User email
   * @param password - User password
   * @returns Promise void
   */
  async function login(email: string, password: string): Promise<void> {
    try {
      setLoading(true)
      const response = await loginService({ email, password })
      setUser(response.user)
    } catch (error: any) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Logs out user
   * @returns Promise void
   */
  async function logout(): Promise<void> {
    try {
      setLoading(true)
      await logoutService()
      setUser(null)
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Refreshes user data
   * @returns Promise void
   */
  async function refreshUser(): Promise<void> {
    try {
      const currentUser = await getCurrentUser()
      setUser(currentUser)
    } catch (error) {
      console.error('Error refreshing user:', error)
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to use auth context
 * @returns Auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
